import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

export type TtsStepData = {
  id: string;
  orderIndex?: number;
  speaker?: string | null;
  character?: string | null;
  text?: string | null;
  audioUrl?: string | null;
};

export type TtsStepEditModalProps = {
  open: boolean;
  step: TtsStepData | null;
  onClose: () => void;
  onReplace: (file: File | Blob) => Promise<void>;
};

function secondsToLabel(sec: number) {
  const safe = Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const totalMs = Math.floor(safe * 1000);
  const s = Math.floor(totalMs / 1000);
  const ms = totalMs % 1000;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

const TtsStepEditModal: React.FC<TtsStepEditModalProps> = ({ open, step, onClose, onReplace }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = React.useState(false);
  // Web Audio kaldırıldı; buffer kullanılmıyor
  const [duration, setDuration] = React.useState(0);
  const [range, setRange] = React.useState<[number, number]>([0, 0]);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [origPosSec, setOrigPosSec] = React.useState(0);
  const [origDurSec, setOrigDurSec] = React.useState(0);
  const [previewPosSec, setPreviewPosSec] = React.useState(0);
  const [previewDurSec, setPreviewDurSec] = React.useState(0);
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);
  const [uploadedInsertAtSec, setUploadedInsertAtSec] = React.useState<number>(0);
  const [silenceSec, setSilenceSec] = React.useState<number>(0);
  const [insertAtSec, setInsertAtSec] = React.useState<number>(0);
  const [insertDurSec, setInsertDurSec] = React.useState<number>(0);
  const [insertList, setInsertList] = React.useState<Array<{ at: number; dur: number }>>([]);
  const [error, setError] = React.useState<string | null>(null);
  // Her zaman backend işleme
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      setError(null);

      if (!open || !step?.audioUrl) { setDuration(0); setRange([0, 0]); return; }
      // Do not block UI on open; just measure metadata when ready
      try {
        const audio = new Audio();
        audio.src = step.audioUrl;
        const onLoaded = () => {
          if (!alive) return;
          const dur = Number.isFinite(audio.duration) ? audio.duration : 0;
          setDuration(dur);
          setRange([0, Math.max(0.01, dur)]);
        };
        audio.addEventListener('loadedmetadata', onLoaded, { once: true });
      } catch (e: any) {
        if (alive) setError(e?.message || 'Ses verisi yüklenemedi');
      } finally {}
    };
    load();
    return () => { alive = false; };
  }, [open, step?.audioUrl]);

  // no-op: Web Audio disabled; all processing via backend

  React.useEffect(() => {
    if (!open || !step) {
      setUploadedFile(null);
      setUploadedInsertAtSec(0);
      setPreviewUrl(null);
      setSilenceSec(0);
      setInsertAtSec(0);
      setInsertDurSec(0);
      setInsertList([]);
      setError(null);
      setRange([0, 0]);
      setDuration(0);
      setOrigPosSec(0);
      setOrigDurSec(0);
      setPreviewPosSec(0);
      setPreviewDurSec(0);
    }
  }, [open, step]);

  const handleTrimPreview = async () => {
    if (loading) return;
    const [start, end] = range;
    if (!step?.audioUrl) { setError('Önizleme için ses bulunamadı'); return; }
    if (!(duration > 0) || end <= start) { setError('Geçersiz süre aralığı'); return; }
    setLoading(true);
    setError(null);
    try {
      if (uploadedFile) {
        const trimmedDurSec = Math.max(0, end - start);
        const normalizedList = [...insertList];
        if (Math.max(0, insertDurSec) > 0) normalizedList.push({ at: insertAtSec, dur: insertDurSec });
        const validList = normalizedList
          .map(x => ({ at: Math.max(0, Math.min(trimmedDurSec, Number(x.at) || 0)), dur: Math.max(0, Number(x.dur) || 0) }))
          .filter(x => x.dur > 0)
          .sort((a,b) => a.at - b.at);

        const fd = new FormData();
        fd.append('audioUrl', step.audioUrl);
        fd.append('trimStartSec', String(start));
        fd.append('trimEndSec', String(end));
        fd.append('tailSilenceSec', String(Math.max(0, silenceSec)));
        fd.append('insertAtSec', String(Math.max(0, uploadedInsertAtSec)));
        if (validList.length > 0) {
          fd.append('insertListJson', JSON.stringify(validList));
        }
        fd.append('file', uploadedFile);

        const url = apiBase ? `${apiBase}/audio/edit-with-upload` : '/api/audio/edit-with-upload';
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 120000);
        const resp = await fetch(url, {
          method: 'POST',
          body: fd,
          signal: controller.signal,
        });
        clearTimeout(timer);
        if (!resp.ok) {
          const txt = await resp.text().catch(() => '');
          throw new Error(txt || 'preview_with_upload_failed');
        }
        const blob = await resp.blob();
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
      } else {
        // Always backend preview
        const trimmedDurSec = Math.max(0, end - start);
        const normalizedList = [...insertList];
        if (Math.max(0, insertDurSec) > 0) normalizedList.push({ at: insertAtSec, dur: insertDurSec });
        const validList = normalizedList
          .map(x => ({ at: Math.max(0, Math.min(trimmedDurSec, Number(x.at) || 0)), dur: Math.max(0, Number(x.dur) || 0) }))
          .filter(x => x.dur > 0)
          .sort((a,b) => a.at - b.at);

        const payload = {
          audioUrl: step.audioUrl,
          trimStartSec: start,
          trimEndSec: end,
          insertList: validList,
          tailSilenceSec: Math.max(0, silenceSec),
        };
        const url = apiBase ? `${apiBase}/audio/edit-preview` : '/api/audio/edit-preview';
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 120000);
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        clearTimeout(timer);
        if (!resp.ok) {
          const txt = await resp.text().catch(() => '');
          throw new Error(txt || 'preview_failed');
        }
        const blob = await resp.blob();
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
      }
    } catch (e: any) {
      setError(`Önizleme oluşturulamadı${e?.message ? `: ${e.message}` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Always backend apply
    setLoading(true);
    try {
      const [start, end] = range;
      const trimmedDurSec = Math.max(0, end - start);
      const normalizedList = [...insertList];
      if (Math.max(0, insertDurSec) > 0) normalizedList.push({ at: insertAtSec, dur: insertDurSec });
      const validList = normalizedList
        .map(x => ({ at: Math.max(0, Math.min(trimmedDurSec, Number(x.at) || 0)), dur: Math.max(0, Number(x.dur) || 0) }))
        .filter(x => x.dur > 0)
        .sort((a,b) => a.at - b.at);

      if (step?.audioUrl) {
        if (uploadedFile) {
          const fd = new FormData();
          fd.append('audioUrl', step.audioUrl);
          fd.append('trimStartSec', String(start));
          fd.append('trimEndSec', String(end));
          fd.append('tailSilenceSec', String(Math.max(0, silenceSec)));
          fd.append('insertAtSec', String(Math.max(0, uploadedInsertAtSec)));
          if (validList.length > 0) {
            fd.append('insertListJson', JSON.stringify(validList));
          }
          fd.append('file', uploadedFile);

          const url = apiBase ? `${apiBase}/audio/edit-with-upload` : '/api/audio/edit-with-upload';
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 120000);
          const resp = await fetch(url, {
            method: 'POST',
            body: fd,
            signal: controller.signal,
          });
          clearTimeout(timer);
          if (!resp.ok) {
            const txt = await resp.text().catch(() => '');
            throw new Error(txt || 'apply_with_upload_failed');
          }
          const blob = await resp.blob();
          await onReplace(blob);
          return;
        }

        const payload = {
          audioUrl: step.audioUrl,
          trimStartSec: start,
          trimEndSec: end,
          insertList: validList,
          tailSilenceSec: Math.max(0, silenceSec),
        };
        const url = apiBase ? `${apiBase}/audio/edit-preview` : '/api/audio/edit-preview';
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 120000);
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        clearTimeout(timer);
        if (!resp.ok) {
          const txt = await resp.text().catch(() => '');
          throw new Error(txt || 'apply_failed');
        }
        const blob = await resp.blob();
        await onReplace(blob);
        return;
      }
      // no-op if no audioUrl
    } finally {
      setLoading(false);
    }
  };

  // marks removed (unused)

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} fullWidth maxWidth="md" fullScreen={fullScreen} scroll="paper">
      <DialogTitle>TTS Parçasını Düzenle</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: fullScreen ? '80dvh' : '70vh', overflow: 'auto' }}>
        <Stack spacing={2}>
          {loading && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}
          {step && (
            <Stack spacing={0.5}>
              <Typography variant="body2"><strong>Sıra:</strong> {step.orderIndex ?? '-'}</Typography>
              <Typography variant="body2"><strong>Speaker:</strong> {step.speaker ?? '-'}</Typography>
              <Typography variant="body2"><strong>Character:</strong> {step.character ?? '-'}</Typography>
              {step.text && <Typography variant="body2"><strong>Text:</strong> {step.text}</Typography>}
            </Stack>
          )}
          {/* Web Audio disabled: no resume UI */}
          {step?.audioUrl && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Orijinal Ses</Typography>
              <audio
                src={step.audioUrl}
                controls
                style={{ width: '100%' }}
                preload="metadata"
                onLoadedMetadata={(e) => {
                  const a = e.currentTarget as HTMLAudioElement;
                  const d = Number.isFinite(a.duration) ? a.duration : 0;
                  setOrigDurSec(d);
                }}
                onTimeUpdate={(e) => {
                  const a = e.currentTarget as HTMLAudioElement;
                  const t = Number.isFinite(a.currentTime) ? a.currentTime : 0;
                  setOrigPosSec(t);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {secondsToLabel(origPosSec)} / {secondsToLabel(origDurSec)}
              </Typography>
            </Stack>
          )}
          {step?.audioUrl && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">Dosyadan Ses Ekle</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                >
                  Cihazdan Ses Seç
                  <input
                    type="file"
                    accept="audio/*"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setUploadedFile(f);
                    }}
                  />
                </Button>
                {uploadedFile && (
                  <Typography variant="caption" color="text.secondary">
                    Seçili dosya: {uploadedFile.name}
                  </Typography>
                )}
              </Stack>
              {uploadedFile && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                  <TextField
                    type="number"
                    label="Yüklenen Sesi Şu Saniyeye Ekle (sn)"
                    inputProps={{ step: 0.5, min: 0 }}
                    value={uploadedInsertAtSec}
                    onChange={(e) => setUploadedInsertAtSec(Math.max(0, Number(e.target.value) || 0))}
                    sx={{ width: 260 }}
                  />
                </Stack>
              )}
            </Stack>
          )}
          {true && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">Baş/son kırp</Typography>
              <Slider
                value={range}
                onChange={(_, v) => Array.isArray(v) && setRange([v[0], v[1]] as any)}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => secondsToLabel(Number(v) || 0)}
                min={0}
                max={Math.max(0.01, duration)}
                step={0.001}
              />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption">Başlangıç: {secondsToLabel(range[0])}</Typography>
                <Typography variant="caption">Bitiş: {secondsToLabel(range[1])}</Typography>           
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                <TextField
                  type="number"
                  label="Sona Sessizlik (sn)"
                  inputProps={{ step: 0.001, min: 0 }}
                  value={silenceSec}
                  onChange={(e) => setSilenceSec(Math.max(0, Number(e.target.value) || 0))}
                  sx={{ width: 200 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Önizleme ve Kaydet sırasında kırpılmış sesin sonuna eklenir.
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                <TextField
                  type="number"
                  label="İçeride Ekleme Zamanı (sn)"
                  inputProps={{ step: 0.001, min: 0 }}
                  value={insertAtSec}
                  onChange={(e) => setInsertAtSec(Math.max(0, Number(e.target.value) || 0))}
                  sx={{ width: 200 }}
                />
                <TextField
                  type="number"
                  label="İçeride Sessizlik Süresi (sn)"
                  inputProps={{ step: 0.001, min: 0 }}
                  value={insertDurSec}
                  onChange={(e) => setInsertDurSec(Math.max(0, Number(e.target.value) || 0))}
                  sx={{ width: 220 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Eğer süre &gt; 0 ise, kırpılmış bölüm içinde bu zamanda sessizlik eklenir.
                </Typography>
              </Stack>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">İçeride çoklu sessizlik ekle</Typography>
                {insertList.map((row, i) => (
                  <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                    <TextField
                      type="number"
                      label={`Ekleme Zamanı #${i+1} (sn)`}
                      inputProps={{ step: 0.001, min: 0 }}
                      value={row.at}
                      onChange={(e) => setInsertList(prev => prev.map((r, j) => j === i ? { ...r, at: Math.max(0, Number(e.target.value) || 0) } : r))}
                      sx={{ width: 200 }}
                    />
                    <TextField
                      type="number"
                      label={`Sessizlik Süresi #${i+1} (sn)`}
                      inputProps={{ step: 0.001, min: 0 }}
                      value={row.dur}
                      onChange={(e) => setInsertList(prev => prev.map((r, j) => j === i ? { ...r, dur: Math.max(0, Number(e.target.value) || 0) } : r))}
                      sx={{ width: 220 }}
                    />
                    <IconButton aria-label="remove" onClick={() => setInsertList(prev => prev.filter((_, j) => j !== i))}><DeleteIcon /></IconButton>
                  </Stack>
                ))}
                <Button startIcon={<AddIcon />} onClick={() => setInsertList(prev => [...prev, { at: 0, dur: 0.5 }])}>Sessizlik Alanı Ekle</Button>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={handleTrimPreview} disabled={loading || !(duration > 0) || !(range[1] > range[0]) || !step?.audioUrl}>Önizleme Oluştur</Button>
                {previewUrl && (
                  <Stack spacing={0.5} sx={{ width: '100%' }}>
                    <audio
                      src={previewUrl}
                      controls
                      style={{ width: '100%' }}
                      preload="metadata"
                      onLoadedMetadata={(e) => {
                        const a = e.currentTarget as HTMLAudioElement;
                        const d = Number.isFinite(a.duration) ? a.duration : 0;
                        setPreviewDurSec(d);
                      }}
                      onTimeUpdate={(e) => {
                        const a = e.currentTarget as HTMLAudioElement;
                        const t = Number.isFinite(a.currentTime) ? a.currentTime : 0;
                        setPreviewPosSec(t);
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {secondsToLabel(previewPosSec)} / {secondsToLabel(previewDurSec)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Kapat</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || !step?.audioUrl}>Kaydet</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TtsStepEditModal;
